import { ComponentProps } from 'react';
import { useGetDocumentTableOfContent } from 'entities/document';
import { useImage, PageArea } from 'entities/document';
import { DataImage } from 'app/components';
import { calulcateCurrentPage } from './utils';
import { useEditorLocale } from 'app/api/documents/useEditorData';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { useEditorLiveSection } from 'app/state/contexts/EditorUiContext';

interface TocHeader {
  uuid: string;
  pageId: string;
  pageNumber: number;
  name?: string;
  [key: string]: unknown;
}

interface OverrideHeading {
  uuid: string;
  text: string | null;
  [key: string]: unknown;
}

interface MenuIconConfig {
  imageRevisionId?: string;
  targetHeaderUuid?: string;
  position?: string;
}

interface AreaOptions {
  pageNumber?: boolean;
  metadataText?: string[];
  menuIcon?: MenuIconConfig;
  [key: string]: unknown;
}

interface MenuIconProps {
  menuIcon?: MenuIconConfig;
  tableOfContent?: TocHeader[];
}

const MenuIcon = ({ menuIcon, tableOfContent }: MenuIconProps) => {
  const { data: imageData } = useImage(menuIcon?.imageRevisionId, 'thumbnail');

  if (!menuIcon?.imageRevisionId || !imageData?.file) {
    return null;
  }

  const targetHeader = tableOfContent?.find((h) => h.uuid === menuIcon?.targetHeaderUuid);
  const href = targetHeader ? `#id-${targetHeader.pageId}` : '#';

  return (
    <li role="presentation" className="menu-icon-item">
      <a role="menuitem" href={href}>
        <DataImage {...(imageData.file as ComponentProps<typeof DataImage>)} style={{ maxHeight: '1.2em', width: 'auto', verticalAlign: 'middle' }} />
      </a>
    </li>
  );
};

interface HeadingsAreaProps {
  pageId: string;
  options: AreaOptions;
}

const HeadingsArea = ({ pageId, options }: HeadingsAreaProps) => {
  const { data: pDocument } = useEditorDocument();
  const locale = useEditorLocale();
  const section = useEditorLiveSection();
  const { data: tableOfContentDocument } = useGetDocumentTableOfContent(pDocument.revisionId ?? null, { locale });
  const { data: tableOfContentSection } = useGetDocumentTableOfContent(pDocument.revisionId ?? null, { locale, sectionSuperId: section.superId });

  if (!tableOfContentDocument?.content) {
    return null;
  }

  const overrideHeadings = ((options[locale] as { overrides?: OverrideHeading[] })?.overrides || []) as OverrideHeading[];
  const excludes = overrideHeadings.filter((override) => override.text === null).map((override) => override.uuid);
  const headers = (tableOfContentDocument.content as TocHeader[]).filter((header) => !excludes.includes(header.uuid));

  const { physicalPage } = calulcateCurrentPage(pDocument, section, pageId, tableOfContentSection);
  const currentRange = calcCurrentRange(headers, physicalPage);

  const menuIcon = options?.menuIcon;
  const iconPosition = menuIcon?.position || 'left';

  const headerItems = headers.map((header) => (
    <li key={header.uuid} role="presentation">
      <a role="menuitem" aria-current={currentRange.includes(header.pageNumber)} href={`#id-${header.pageId}`}>
        {overrideHeadings.find((override) => override.uuid === header.uuid)?.text || header.name}
      </a>
    </li>
  ));

  return (
    <div className="subarea-headers">
      <ol role="menu" aria-orientation="horizontal" style={{ userSelect: 'none' }}>
        {iconPosition === 'left' && <MenuIcon menuIcon={menuIcon} tableOfContent={tableOfContentDocument.content as TocHeader[]} />}
        {headerItems}
        {iconPosition === 'right' && <MenuIcon menuIcon={menuIcon} tableOfContent={tableOfContentDocument.content as TocHeader[]} />}
      </ol>
    </div>
  );
};

const calcCurrentRange = (headers: TocHeader[], physicalPage: number) => {
  let startRange = 0;
  let endRange = 0;

  for (const header of headers) {
    if (header.pageNumber <= physicalPage) {
      startRange = header.pageNumber;
      endRange = header.pageNumber;
    }
    if (header.pageNumber > physicalPage) {
      endRange = header.pageNumber;
      break;
    }
  }

  return Array.from({ length: endRange - startRange || 1 }, (_, index) => startRange + index);
};

interface AreaProps {
  pageId: string;
  areaConfig: PageArea | null | undefined;
}

const Area = ({ pageId, areaConfig }: AreaProps) => {
  if (!areaConfig) {
    return null;
  }
  switch (areaConfig.type) {
    case 'simple':
      return <SimpleArea pageId={pageId} options={areaConfig.options as AreaOptions} />;
    case 'headings':
      return <HeadingsArea pageId={pageId} options={areaConfig.options as AreaOptions} />;
    case 'combined':
      return <CombinedArea pageId={pageId} options={areaConfig.options as AreaOptions} />;
    default:
      return null;
  }
};

const PageNumber = ({ pageId }: { pageId: string }) => {
  const { data: pDocument } = useEditorDocument();
  const locale = useEditorLocale();
  const section = useEditorLiveSection();
  const { data, isLoading } = useGetDocumentTableOfContent(pDocument.revisionId ?? null, { locale, sectionSuperId: section.superId });

  if (isLoading) {
    return null;
  }

  const pageNumbering = pDocument.content?.pageNumbering;

  const { physicalPage, documentPage } = calulcateCurrentPage(pDocument, section, pageId, data);

  if (pageNumbering && physicalPage < pageNumbering.showFrom) {
    return <div className="subarea-pagenumber"></div>;
  }

  return <div className="subarea-pagenumber">{documentPage || null}</div>;
};

const Texts = ({ textKeys }: { textKeys: string[] }) => {
  const { data: pDocument } = useEditorDocument();
  const locale = useEditorLocale();

  const texts = pDocument.content?.metadata?.texts?.[locale] ?? {};

  const usedTexts = textKeys.filter((textKey) => texts.hasOwnProperty(textKey));

  return (
    <div className="subarea-texts">
      {usedTexts.map((textKey) => (
        <div key={textKey} className={`subarea-text-${textKey}`}>
          {texts[textKey] ?? ''}
        </div>
      ))}
    </div>
  );
};

interface SimpleAreaProps {
  pageId: string;
  options: AreaOptions;
}

const SimpleArea = ({ pageId, options }: SimpleAreaProps) => {
  const { pageNumber, metadataText } = options;

  return (
    <>
      {pageNumber ? <PageNumber pageId={pageId} /> : null}
      {metadataText && metadataText.length > 0 ? <Texts textKeys={metadataText} /> : null}
    </>
  );
};

const CombinedArea = ({ pageId, options = {} }: { pageId: string; options?: AreaOptions }) => {
  const { pageNumber, metadataText } = options;

  return (
    <>
      <HeadingsArea pageId={pageId} options={options} />
      {metadataText && metadataText.length > 0 ? <Texts textKeys={metadataText} /> : null}
      {pageNumber ? <PageNumber pageId={pageId} /> : null}
    </>
  );
};

export default Area;
